// 氏名の第 2 経路 (spec: docs/superpowers/specs/2026-08-20-name-second-path-and-ranking-design.md
// §3.2 / §4.1)。氏名の発話を ElevenLabs の batch STT にもう一度かけ、2 つ目の書き起こしを得る。
// 中継は functions/api/stt/batch.ts (鍵をブラウザに出せないため)。
//
// **この関数は throw しない。** 失敗はすべて null に倒れ、呼び出し側は「1 経路で
// 順位付けする」= 現行と同じ形に落ちる (原則 5)。呼び出し側 (NameCapture の
// startEnrichment) に catch を書かせないのが契約である。
//
// **app.error は出さない (D21)。** app.error は CRITICAL_TYPES かつ 10 件/session で、
// 第 2 経路と順位付けがこれを踏むと MAX_RECOGNITIONS = 6 × 2 経路 = 最大 12 件が
// 氏名 step だけで発生しうる。回線の悪いセッションでは**本編に入る前に上限を使い切り**、
// 以降の structure / tts / stt の app.error が黙って落ちる (上限に達したこと自体は
// 記録されないので気づけない)。失敗の観測点は preflight.name_second_asr.outcome (D27)。
//
// **SttMetric も出さない (D14 / §5.5)。** SttMetric.provider は literal "elevenlabs" で、
// summary.stt は全件を混ぜて meanRoundTripMs / p95 を出し model は最初に観測したものに
// なる。realtime と batch を混ぜると二峰になり、事前生成 TTS で一度踏んだ形を再現する。
// 往復は preflight.name_second_asr.ms が 1 件ずつ持つ。

/**
 * 氏名の発話を第 2 の音声認識にかける。
 *
 * @param wav 生の WAV (16 kHz mono PCM16)。base64 にしない — 33% 増える
 * @param signal 呼び出し側が打ち切るための signal (却下・STT 失敗・stale)
 * @returns 書き起こし (前後の空白を落としたもの)。失敗・空はすべて null
 */
export async function transcribeNameAudio(wav: Blob, signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch("/api/stt/batch", {
      method: "POST",
      headers: { "content-type": "audio/wav" },
      body: wav,
      signal,
    });
    // 500 (鍵が無い) / 502 (上流エラー) / 413 (サイズ超過) / 415 — どれも同じ倒れ先。
    if (!res.ok) return null;
    const json = (await res.json()) as { text?: unknown };
    // 実測 (el-batch-probe.py) は `(res.get("text") or "").strip()` で採点している。
    // 同じ形にしないと測った数字 (Scribe + batch で 3 回以上の言い直しが 6/16 → 3/16)
    // がそのまま移らない。
    const text = typeof json.text === "string" ? json.text.trim() : "";
    return text.length > 0 ? text : null;
  } catch {
    // 回線断 / DNS 失敗 / abort / 200 だが JSON でない (dev の SPA fallback)。
    return null;
  }
}
